import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { baseUrl, R2_PUBLIC_URL } from '../config';
import SEO from '../components/SEO';
import toast from 'react-hot-toast';
import headerBg from '../assets/img/JobPosting.jpg';
import formBg from '../assets/img/createproject.jpg';

// ==================== JOB ACTION ENDPOINTS ====================
// ⚠️ Verify these two routes exist in the PB Main backend before shipping.
// If your routes are named differently, change them here only — nothing else needs to change.
const JOB_API = {
  update: (id) => `${baseUrl}/updateJobPosting/${id}`, // PUT  — JSON body with changed fields (edit + status)
  remove: (id) => `${baseUrl}/deleteJobPosting/${id}`, // DELETE
};

const PAGE_SIZE = 10;
const MENU_WIDTH = 200;
const GENRES = ['Action', 'Comedy', 'Drama', 'Horror', 'Romance', 'Sci-Fi', 'Documentary', 'Thriller', 'Other'];

const getJobDate = (job) =>
  job.created_at || job.posted_on || job.createdAt || job.date_posted || null;

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const initialsOf = (title) => {
  if (!title) return 'P';
  const parts = title.trim().split(/\s+/);
  return (parts[0].charAt(0) + (parts[1] ? parts[1].charAt(0) : '')).toUpperCase();
};

// ==================== ICONS (inline SVG) ====================
const PATHS = {
  plus: (<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>),
  folder: (<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />),
  chevronRight: (<polyline points="9 18 15 12 9 6" />),
  chevronLeft: (<polyline points="15 18 9 12 15 6" />),
  arrowLeft: (<><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></>),
  search: (<><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>),
  filter: (<polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />),
  eye: (<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>),
  more: (<><circle cx="12" cy="5" r="1.2" /><circle cx="12" cy="12" r="1.2" /><circle cx="12" cy="19" r="1.2" /></>),
  edit: (<><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" /></>),
  ban: (<><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></>),
  trash: (<><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></>),
  check: (<polyline points="20 6 9 17 4 12" />),
  mapPin: (<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>),
  x: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>),
  send: (<><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></>),
  pencil: (<path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />),
};

const Ico = ({ name, size = 18, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
    {PATHS[name]}
  </svg>
);

const JobPostings = () => {
  const navigate = useNavigate();

  // State
  const [jobs, setJobs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOptionsOpen, setSortOptionsOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  // New UI state
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editingJob, setEditingJob] = useState(null);
  const [menu, setMenu] = useState(null); // { job, top, left, flipUp }
  const [statusSubOpen, setStatusSubOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const menuRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    project_id: null,
    job_title: '',
    role: '',
    description: '',
    full_description: '',
    location: '',
    job_type: 'Full-time',
    salary: '',
    genre: '',
    application_deadline: '',
    project_title: '',
    project_description: '',
    project_logo: ''
  });

  // Filter states
  const [selectedRoles, setSelectedRoles] = useState({});
  const [selectedStatuses, setSelectedStatuses] = useState({
    Open: false,
    Closed: false,
  });

  // Fetch user's projects on mount
  useEffect(() => {
    fetchProjects();
    fetchJobs();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem("access_token");

      // Get user ID
      const userIdResponse = await fetch(`${baseUrl}/getUserIDByToken`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (userIdResponse.ok) {
        const { user_id } = await userIdResponse.json();

        // Get project IDs for this user
        const projectIdsResponse = await fetch(`${baseUrl}/getProjectsByUserId`, {
          headers: { 'user_id': user_id }
        });

        if (projectIdsResponse.ok) {
          const projectIdsData = await projectIdsResponse.json();

          // ✅ FETCH FULL PROJECT DATA FOR EACH PROJECT (like ToolManager does!)
          let projectInfoTemp = [];
          for (let index = 0; index < projectIdsData.length; index++) {
            const element = projectIdsData[index];

            // Fetch full project details
            const projInfRequest = await fetch(`${baseUrl}/getProjectById`, {
              headers: {
                "project_id": element.project_id
              }
            });

            if (projInfRequest.ok) {
              const projInfResponse = await projInfRequest.json();
              projectInfoTemp.push(projInfResponse);
            }
          }

          setProjects(projectInfoTemp);
        }
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${baseUrl}/getJobPostingsByProductionHouse`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();

        setJobs(data);

        // Build dynamic role filters (keeps roles already ticked after a refresh)
        setSelectedRoles(prev => {
          const roles = {};
          data.forEach(job => {
            roles[job.role] = prev[job.role] || false;
          });
          return roles;
        });
      }
    } catch (err) {
    }
  };

  // Project tile click - auto-fill form
  const handleProjectSelect = (project) => {
    setEditingJob(null);
    setSelectedProject(project);
    setFormData({
      ...formData,
      // project records expose project_id; id is not always present
      project_id: project.id ?? project.project_id ?? null,
      project_title: project.title || '',
      project_description: project.project_desc || '',
      project_logo: project.project_image || '',
      genre: project.genre || ''
    });
    setModalOpen(true);
  };

  // Manual job posting (no project selected)
  const handleManualPost = () => {
    setEditingJob(null);
    setSelectedProject(null);
    setShowProjectPicker(false);
    setFormData({
      project_id: null,
      job_title: '',
      role: '',
      description: '',
      full_description: '',
      location: '',
      job_type: 'Full-time',
      salary: '',
      genre: '',
      application_deadline: '',
      project_title: '',
      project_description: '',
      project_logo: ''
    });
    setModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePostJob = async () => {
    if (!formData.job_title || !formData.role || !formData.description) {
      alert('Please fill in required fields: Job Title, Role, and Description');
      return;
    }
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${baseUrl}/createJobPosting`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        alert('Job posted successfully!');
        setModalOpen(false);
        setShowProjectPicker(false);
        fetchJobs(); // Refresh list
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (err) {
      alert('Failed to post job. Please try again.');
    }
  };

  const handleViewApplications = (job) => {
    navigate(`/job-applications/${job.id}`, {
      state: {
        jobTitle: job.job_title,
        jobId: job.id
      }
    });
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    if (selectedRoles.hasOwnProperty(name)) {
      setSelectedRoles(prev => ({ ...prev, [name]: checked }));
    } else {
      setSelectedStatuses(prev => ({ ...prev, [name]: checked }));
    }
  };

  // ==================== NEW: job actions (⋮ menu) ====================
  const closeMenu = () => {
    setMenu(null);
    setStatusSubOpen(false);
  };

  const closeForm = () => {
    setModalOpen(false);
    setEditingJob(null);
  };

  const updateJob = async (job, changes, successMsg) => {
    setActionBusy(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(JOB_API.update(job.id), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(changes)
      });
      if (response.ok) {
        toast.success(successMsg);
        await fetchJobs();
        return true;
      }
      if (response.status === 404 || response.status === 405) {
        toast.error("This action isn't available yet. Please try again later.");
      } else {
        toast.error("We couldn't update this job. Please try again.");
      }
      return false;
    } catch (err) {
      toast.error("We couldn't update this job. Please try again.");
      return false;
    } finally {
      setActionBusy(false);
    }
  };

  const handleStatusChange = async (job, status) => {
    closeMenu();
    if (job.status === status) return;
    await updateJob(job, { status }, status === 'Closed' ? 'Job closed' : `Job marked as ${status}`);
  };

  const handleDeleteJob = async () => {
    const job = confirmDelete;
    if (!job) return;
    setActionBusy(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(JOB_API.remove(job.id), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Job deleted');
        setConfirmDelete(null);
        await fetchJobs();
      } else if (response.status === 404 || response.status === 405) {
        toast.error("This action isn't available yet. Please try again later.");
      } else {
        toast.error("We couldn't delete this job. Please try again.");
      }
    } catch (err) {
      toast.error("We couldn't delete this job. Please try again.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleEditJob = (job) => {
    closeMenu();
    setEditingJob(job);
    setSelectedProject(null);
    setFormData({
      project_id: job.project_id ?? null,
      job_title: job.job_title || '',
      role: job.role || '',
      description: job.description || '',
      full_description: job.full_description || '',
      location: job.location || '',
      job_type: job.job_type || 'Full-time',
      salary: job.salary || '',
      genre: job.genre || '',
      application_deadline: job.application_deadline ? String(job.application_deadline).slice(0, 10) : '',
      project_title: job.project_title || '',
      project_description: job.project_description || '',
      project_logo: job.project_logo || ''
    });
    setModalOpen(true);
  };

  const handleUpdateJob = async () => {
    if (!formData.job_title || !formData.role || !formData.description) {
      toast.error('Please fill in Job Title, Role and Short Description.');
      return;
    }
    const ok = await updateJob(editingJob, formData, 'Job updated');
    if (ok) closeForm();
  };

  // ⋮ menu is position:fixed so the scrolling table never clips it
  const openMenu = (e, job) => {
    if (menu && menu.job.id === job.id) { closeMenu(); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const estHeight = 190;
    const flipUp = r.bottom + 6 + estHeight > window.innerHeight;
    setStatusSubOpen(false);
    setMenu({
      job,
      left: Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)),
      top: flipUp ? r.top - 6 : r.bottom + 6,
      flipUp,
    });
  };

  useEffect(() => {
    if (!menu) return;
    const onDown = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-jp-kebab]')) return;
      closeMenu();
    };
    const onKey = (e) => { if (e.key === 'Escape') closeMenu(); };
    const onScrollOrResize = () => closeMenu();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [menu]);

  // Each screen starts at the top
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [modalOpen, showProjectPicker]);

  // Filter and sort jobs
  const term = searchTerm.toLowerCase();
  const filteredJobs = jobs.filter(job => {
    const matchesSearch = [job.job_title, job.role, job.project_title, job.location]
      .filter(Boolean)
      .some(v => String(v).toLowerCase().includes(term));

    const roleChecked = selectedRoles[job.role] || false;
    const statusChecked = selectedStatuses[job.status] || false;

    const matchesRole = roleChecked || Object.values(selectedRoles).every(v => !v);
    const matchesStatus = statusChecked || Object.values(selectedStatuses).every(v => !v);

    return matchesSearch && matchesRole && matchesStatus;
  });

  // Back to page 1 whenever search / filters change
  useEffect(() => { setPage(1); }, [searchTerm, selectedRoles, selectedStatuses]);

  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pagedJobs = filteredJobs.slice(pageStart, pageStart + PAGE_SIZE);
  const activeFilterCount =
    Object.values(selectedRoles).filter(Boolean).length +
    Object.values(selectedStatuses).filter(Boolean).length;

  const filteredProjects = projects.filter(p =>
    [p.title, p.project_desc, p.genre].filter(Boolean)
      .some(v => String(v).toLowerCase().includes(projectSearch.toLowerCase()))
  );

  // ==================== RENDER: project avatar ====================
  const renderProjectAvatar = (project) => (
    <div className="jp-avatar">
      {project.project_image ? (
        <img
          src={`${R2_PUBLIC_URL}/img/${project.project_image}`}
          alt={project.title || 'Project'}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      ) : null}
      <span style={{ display: project.project_image ? 'none' : 'flex' }}>
        {initialsOf(project.title)}
      </span>
    </div>
  );

  // ==================== RENDER: form screen ====================
  const renderForm = () => (
    <div className="jp-form-page">
      <div className="jp-form-bg" style={{ backgroundImage: `url(${formBg})` }} />
      <div className="jp-form-overlay" />
      <div className="jp-form-wrap">
        <button type="button" className="jp-back" onClick={closeForm}>
          <Ico name="arrowLeft" size={16} /> Back
        </button>

        <div className="jp-form-card">
          <div className="jp-form-head">
            <div>
              <h2 className="jp-form-title">
                {editingJob ? 'Edit job' : selectedProject ? 'Post a job for this project' : 'Post a new job'}
              </h2>
              <p className="jp-form-sub">
                {editingJob ? 'Update the details of this job posting.' : 'Fill in the details to reach the right talent for your project.'}
              </p>
            </div>
            <button type="button" className="jp-icon-btn" onClick={closeForm} aria-label="Close">
              <Ico name="x" size={18} />
            </button>
          </div>

          {/* Show selected project info */}
          {selectedProject && (
            <div className="jp-selected-project">
              {renderProjectAvatar(selectedProject)}
              <div className="jp-selected-text">
                <p className="jp-selected-title">{selectedProject.title}</p>
                {selectedProject.project_desc && <p className="jp-selected-desc">{selectedProject.project_desc}</p>}
              </div>
            </div>
          )}

          <form className="jp-form" onSubmit={(e) => e.preventDefault()}>
            <p className="jp-form-section">Basic information</p>

            <div className="jp-grid-2">
              <div className="jp-field">
                <label htmlFor="jp-title">Job Title<span className="jp-req">*</span></label>
                <input id="jp-title" type="text" name="job_title" placeholder="e.g., Lead Camera Operator"
                  value={formData.job_title} onChange={handleInputChange}
                  className="jp-input job-post-job-title-input" required />
              </div>
              <div className="jp-field">
                <label htmlFor="jp-role">Role / Department<span className="jp-req">*</span></label>
                <select id="jp-role" name="role" value={formData.role} onChange={handleInputChange}
                  className="jp-input jp-select job-post-role-select" required>
                  <option value="">Select Role</option>
                  <option value="Cast">Cast/Actor</option>
                  <option value="Director">Director</option>
                  <option value="Camera Operator">Camera Operator</option>
                  <option value="Sound Engineer">Sound Engineer</option>
                  <option value="Editor">Editor</option>
                  <option value="Production Assistant">Production Assistant</option>
                  <option value="Gaffer">Gaffer/Lighting</option>
                  <option value="Makeup Artist">Makeup Artist</option>
                  <option value="Costume Designer">Costume Designer</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="jp-field">
              <label htmlFor="jp-desc">Short Description<span className="jp-req">*</span></label>
              <div className="jp-icon-field">
                <Ico name="pencil" size={15} className="jp-field-icon" />
                <textarea id="jp-desc" name="description" placeholder="Brief overview of the role..."
                  value={formData.description} onChange={handleInputChange} rows="3"
                  className="jp-input jp-textarea job-post-description-textarea" required />
              </div>
            </div>

            <div className="jp-field">
              <label htmlFor="jp-full">Full Job Description</label>
              <div className="jp-icon-field">
                <Ico name="pencil" size={15} className="jp-field-icon" />
                <textarea id="jp-full" name="full_description"
                  placeholder="Detailed requirements, responsibilities, qualifications..."
                  value={formData.full_description} onChange={handleInputChange} rows="5"
                  className="jp-input jp-textarea job-post-full-description-textarea" />
              </div>
            </div>

            <p className="jp-form-section">Project &amp; details</p>

            <div className="jp-grid-2">
              <div className="jp-field">
                <label htmlFor="jp-loc">Location</label>
                <input id="jp-loc" type="text" name="location" placeholder="e.g., Mumbai, MH"
                  value={formData.location} onChange={handleInputChange}
                  className="jp-input job-post-location-input" />
              </div>
              <div className="jp-field">
                <label htmlFor="jp-type">Job Type</label>
                <select id="jp-type" name="job_type" value={formData.job_type} onChange={handleInputChange}
                  className="jp-input jp-select job-post-type-select">
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Freelance">Freelance</option>
                </select>
              </div>
              <div className="jp-field">
                <label htmlFor="jp-salary">Salary Range</label>
                <input id="jp-salary" type="text" name="salary" placeholder="e.g., ₹50,000 - ₹70,000"
                  value={formData.salary} onChange={handleInputChange}
                  className="jp-input job-post-salary-input" />
              </div>
              <div className="jp-field">
                <label htmlFor="jp-genre">Genre</label>
                <select id="jp-genre" name="genre" value={formData.genre} onChange={handleInputChange}
                  className="jp-input jp-select job-post-genre-select">
                  <option value="">Select Genre</option>
                  {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                  {/* keep a project's own genre selectable even if it isn't in the list */}
                  {formData.genre && !GENRES.includes(formData.genre) && (
                    <option value={formData.genre}>{formData.genre}</option>
                  )}
                </select>
              </div>
              <div className="jp-field">
                <label htmlFor="jp-deadline">Application Deadline</label>
                <input id="jp-deadline" type="date" name="application_deadline"
                  value={formData.application_deadline} onChange={handleInputChange}
                  className="jp-input job-post-deadline-input" />
              </div>
            </div>

            <div className="jp-form-actions">
              <button type="button" onClick={closeForm} className="jp-btn jp-btn-ghost">
                Cancel
              </button>
              <button type="button" onClick={editingJob ? handleUpdateJob : handlePostJob}
                disabled={actionBusy} className="jp-btn jp-btn-primary">
                <Ico name="send" size={16} /> {editingJob ? (actionBusy ? 'Saving...' : 'Save changes') : 'Post Job'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // ==================== RENDER: project picker screen ====================
  const renderProjectPicker = () => (
    <div className="jp-body jp-picker">
      <button type="button" className="jp-back" onClick={() => setShowProjectPicker(false)}>
        <Ico name="arrowLeft" size={16} /> Back to job postings
      </button>

      <div className="jp-picker-head">
        <div>
          <h2 className="jp-page-title">Post from a project</h2>
          <p className="jp-page-sub">Pick a project — its title, description and genre are filled in for you.</p>
        </div>
        {projects.length > 0 && (
          <div className="jp-search jp-search--sm">
            <Ico name="search" size={16} className="jp-search-icon" />
            <input type="text" value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)}
              placeholder="Search projects..." className="jp-input jp-search-input" aria-label="Search projects" />
          </div>
        )}
      </div>

      {loading ? (
        <div className="jp-project-grid">
          <div className="jp-skeleton" /><div className="jp-skeleton" /><div className="jp-skeleton" />
        </div>
      ) : projects.length === 0 ? (
        <div className="jp-empty">
          <span className="jp-empty-icon"><Ico name="folder" size={26} /></span>
          <p className="jp-empty-title">No projects yet</p>
          <p className="jp-empty-sub">Create a project first, or post this job without one.</p>
          <button type="button" className="jp-btn jp-btn-primary" onClick={handleManualPost}>
            <Ico name="plus" size={16} /> Post without a project
          </button>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="jp-empty jp-empty--inline">
          <p>No projects match "{projectSearch}".</p>
          <button type="button" className="jp-link" onClick={() => setProjectSearch('')}>Clear search</button>
        </div>
      ) : (
        <div className="jp-project-grid">
          {filteredProjects.map((project, index) => (
            <button type="button" key={project.id ?? project.project_id ?? index}
              onClick={() => handleProjectSelect(project)} className="jp-project">
              {renderProjectAvatar(project)}
              <div className="jp-project-text">
                <p className="jp-project-title">{project.title || 'Untitled Project'}</p>
                <p className="jp-project-desc">{project.project_desc || 'No description'}</p>
                {project.genre && <span className="jp-chip">{project.genre}</span>}
              </div>
              <Ico name="chevronRight" size={18} className="jp-project-chevron" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ==================== RENDER: main list screen ====================
  const renderList = () => (
    <>
      <header className="jp-hero">
        <div className="jp-hero-bg" style={{ backgroundImage: `url(${headerBg})` }} />
        <div className="jp-hero-overlay" />
        <div className="jp-hero-content">
          <h1 className="jp-hero-title">Find the right people for your projects</h1>
          <p className="jp-hero-sub">Post crew needs, discover talented professionals, and bring your vision to life.</p>
        </div>
      </header>

      <div className="jp-body">
        {/* Two ways to post */}
        <div className="jp-options">
          <button type="button" className="jp-option jp-option--primary" onClick={handleManualPost}>
            <span className="jp-option-tile"><Ico name="plus" size={22} /></span>
            <span className="jp-option-text">
              <span className="jp-option-title">Post a new job</span>
              <span className="jp-option-sub">Manual job posting — post without selecting a project</span>
            </span>
            <Ico name="chevronRight" size={18} className="jp-option-chevron" />
          </button>

          <button type="button" className="jp-option" onClick={() => { setProjectSearch(''); setShowProjectPicker(true); }}>
            <span className="jp-option-tile jp-option-tile--alt"><Ico name="folder" size={22} /></span>
            <span className="jp-option-text">
              <span className="jp-option-title">Post from a project</span>
              <span className="jp-option-sub">
                {loading
                  ? 'Loading your projects...'
                  : `Choose from your ${projects.length} project${projects.length === 1 ? '' : 's'} — details auto-filled`}
              </span>
            </span>
            <Ico name="chevronRight" size={18} className="jp-option-chevron" />
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="jp-toolbar">
          <div className="jp-search">
            <Ico name="search" size={16} className="jp-search-icon" />
            <input type="text" placeholder="Search by job title, role, project, or location..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="jp-input jp-search-input" aria-label="Search job postings" />
          </div>
          <button type="button" className={`jp-btn jp-btn-ghost jp-filter-btn ${sortOptionsOpen ? 'is-open' : ''}`}
            onClick={() => setSortOptionsOpen(prev => !prev)} aria-expanded={sortOptionsOpen}>
            <Ico name="filter" size={16} /> Filters
            {activeFilterCount > 0 && <span className="jp-filter-count">{activeFilterCount}</span>}
          </button>
        </div>

        {/* Filter Options */}
        {sortOptionsOpen && (
          <div className="jp-filters">
            <div>
              <p className="jp-filters-title">Filter by role</p>
              {Object.keys(selectedRoles).length > 0 ? (
                <div className="jp-checks">
                  {Object.keys(selectedRoles).map((role) => (
                    <label key={role} className="jp-check">
                      <input type="checkbox" name={role} checked={selectedRoles[role]} onChange={handleCheckboxChange} />
                      {role}
                    </label>
                  ))}
                </div>
              ) : (
                <p className="jp-muted">No roles yet</p>
              )}
            </div>
            <div>
              <p className="jp-filters-title">Filter by status</p>
              <div className="jp-checks">
                {Object.keys(selectedStatuses).map((status) => (
                  <label key={status} className="jp-check">
                    <input type="checkbox" name={status} checked={selectedStatuses[status]} onChange={handleCheckboxChange} />
                    {status}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Jobs Table */}
        <div className="jp-table-card">
          <div className="jp-table-scroll">
            <table className="jp-table">
              <thead>
                <tr>
                  <th>Job Title</th>
                  <th>Role</th>
                  <th>Project</th>
                  <th>Status</th>
                  <th className="jp-center">Applications</th>
                  <th>Posted on</th>
                  <th className="jp-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="jp-table-empty">
                      {jobs.length === 0
                        ? 'No job postings yet. Choose "Post a new job" or "Post from a project" to get started.'
                        : 'No job postings match your search or filters.'}
                    </td>
                  </tr>
                ) : (
                  pagedJobs.map((job) => {
                    const posted = formatDate(getJobDate(job));
                    const isOpen = job.status === 'Open';
                    const menuOpenHere = !!(menu && menu.job.id === job.id);
                    return (
                      <tr key={job.id}>
                        <td>
                          <p className="jp-job-title">{job.job_title}</p>
                          {job.location && (
                            <p className="jp-job-loc"><Ico name="mapPin" size={12} /> {job.location}</p>
                          )}
                        </td>
                        <td>{job.role}</td>
                        <td>{job.project_title || <span className="jp-muted">No project</span>}</td>
                        <td>
                          <span className={`jp-status ${isOpen ? 'jp-status--open' : 'jp-status--closed'}`}>
                            {job.status}
                          </span>
                        </td>
                        <td className="jp-center">{job.application_count || 0}</td>
                        <td>{posted || <span className="jp-muted">—</span>}</td>
                        <td className="jp-right">
                          <div className="jp-actions">
                            <button type="button" onClick={() => handleViewApplications(job)} className="jp-btn jp-btn-primary jp-btn-sm">
                              <Ico name="eye" size={15} /> View applications
                            </button>
                            <button type="button" data-jp-kebab className={`jp-kebab ${menuOpenHere ? 'is-open' : ''}`}
                              onClick={(e) => openMenu(e, job)} aria-label={`More actions for ${job.job_title}`}
                              aria-haspopup="menu" aria-expanded={menuOpenHere}>
                              <Ico name="more" size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {filteredJobs.length > 0 && (
            <div className="jp-table-foot">
              <span className="jp-muted">
                Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredJobs.length)} of {filteredJobs.length} job posting{filteredJobs.length === 1 ? '' : 's'}
              </span>
              <div className="jp-pager">
                <button type="button" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1} aria-label="Previous page">
                  <Ico name="chevronLeft" size={16} />
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button type="button" key={i} onClick={() => setPage(i + 1)}
                    className={currentPage === i + 1 ? 'is-active' : ''} aria-current={currentPage === i + 1 ? 'page' : undefined}>
                    {i + 1}
                  </button>
                ))}
                <button type="button" onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Next page">
                  <Ico name="chevronRight" size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="jp-root">
      <style>{JP_STYLES}</style>
      <SEO
        title="Film Job Postings"
        description="Post and manage job openings for your film production. Find talent for every role in your project on ProducerBuddy."
        url="https://producerbuddy.pilomation.com/jobpostings"
        robots="noindex, nofollow"
      />

      {modalOpen ? renderForm() : showProjectPicker ? renderProjectPicker() : renderList()}

      {/* ⋮ actions menu */}
      {menu && (
        <div ref={menuRef} className="jp-menu" role="menu"
          style={{ left: menu.left, top: menu.top, transform: menu.flipUp ? 'translateY(-100%)' : 'none' }}>
          <button type="button" role="menuitem" className="jp-menu-item" onClick={() => handleEditJob(menu.job)}>
            <Ico name="edit" size={16} /> Edit job
          </button>
          <button type="button" role="menuitem" className="jp-menu-item" onClick={() => setStatusSubOpen(v => !v)}
            aria-expanded={statusSubOpen}>
            <span className={`jp-dot ${menu.job.status === 'Open' ? 'jp-dot--open' : 'jp-dot--closed'}`} /> Change status
            <Ico name="chevronRight" size={14} className={`jp-menu-caret ${statusSubOpen ? 'is-open' : ''}`} />
          </button>
          {statusSubOpen && (
            <div className="jp-submenu">
              {['Open', 'Closed'].map(s => (
                <button key={s} type="button" role="menuitemradio" aria-checked={menu.job.status === s}
                  className="jp-menu-item jp-menu-sub" disabled={actionBusy}
                  onClick={() => handleStatusChange(menu.job, s)}>
                  {s}
                  {menu.job.status === s && <Ico name="check" size={14} className="jp-menu-check" />}
                </button>
              ))}
            </div>
          )}
          <button type="button" role="menuitem" className="jp-menu-item"
            disabled={menu.job.status === 'Closed' || actionBusy}
            onClick={() => handleStatusChange(menu.job, 'Closed')}>
            <Ico name="ban" size={16} className="jp-danger-ico" /> Close job
          </button>
          <button type="button" role="menuitem" className="jp-menu-item jp-menu-danger"
            onClick={() => { const j = menu.job; closeMenu(); setConfirmDelete(j); }}>
            <Ico name="trash" size={16} /> Delete job
          </button>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="jp-dialog-backdrop" onClick={() => !actionBusy && setConfirmDelete(null)}>
          <div className="jp-dialog" role="alertdialog" aria-modal="true" aria-labelledby="jp-del-title"
            onClick={(e) => e.stopPropagation()}>
            <span className="jp-dialog-icon"><Ico name="trash" size={22} /></span>
            <h3 id="jp-del-title" className="jp-dialog-title">Delete this job?</h3>
            <p className="jp-dialog-text">
              "{confirmDelete.job_title}" will be permanently removed. This can't be undone.
            </p>
            <div className="jp-dialog-actions">
              <button type="button" className="jp-btn jp-btn-ghost" onClick={() => setConfirmDelete(null)} disabled={actionBusy}>
                Cancel
              </button>
              <button type="button" className="jp-btn jp-btn-danger" onClick={handleDeleteJob} disabled={actionBusy}>
                {actionBusy ? 'Deleting...' : 'Delete job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== STYLES (scoped jp- classes, kept in this file) ====================
const JP_STYLES = `
.jp-root {
  --jp-teal: #0fb3ad; --jp-teal-hover: #0c9a95; --jp-teal-light: #5eead4;
  --jp-teal-soft: rgba(15,179,173,0.12); --jp-teal-border: rgba(15,179,173,0.45);
  --jp-bg: #111827; --jp-surface: #1b2433; --jp-surface-2: #222d3f;
  --jp-border: rgba(255,255,255,0.08); --jp-text: #f3f5f7; --jp-muted: #94a3b8;
  background: var(--jp-bg); color: var(--jp-text); min-height: calc(100vh - 130px);
}
.jp-root button { font-family: inherit; }
.jp-root button:focus-visible, .jp-root input:focus-visible, .jp-root select:focus-visible, .jp-root textarea:focus-visible {
  outline: 2px solid var(--jp-teal-light); outline-offset: 2px;
}
.jp-muted { color: var(--jp-muted); }

/* Hero */
.jp-hero { position: relative; height: 230px; overflow: hidden; }
.jp-hero-bg { position: absolute; inset: 0; background-size: cover; background-position: center 45%; filter: brightness(1.08) saturate(1.15); }
.jp-hero-overlay { position: absolute; inset: 0; background:
  linear-gradient(90deg, rgba(8,15,25,0.92) 0%, rgba(8,15,25,0.62) 40%, rgba(8,15,25,0.08) 75%, rgba(8,15,25,0.2) 100%),
  linear-gradient(180deg, rgba(17,24,39,0) 55%, rgba(17,24,39,1) 100%); }
.jp-hero-content { position: relative; z-index: 1; height: 100%; display: flex; flex-direction: column; justify-content: center; padding: 0 2rem; }
.jp-hero-title { margin: 0; max-width: 420px; font-size: 28px; font-weight: 700; line-height: 1.25; color: #fff; }
.jp-hero-sub { margin: 8px 0 0; max-width: 440px; font-size: 15px; line-height: 1.5; color: #cbd5e1; }

/* Body */
.jp-body { padding: 1.25rem 2rem 2.5rem; }
.jp-back { display: inline-flex; align-items: center; gap: 6px; padding: 0; margin-bottom: 16px; border: none; background: none;
  color: var(--jp-teal-light); font-size: 14px; font-weight: 500; cursor: pointer; }
.jp-back:hover { color: #fff; }
.jp-page-title { margin: 0; font-size: 22px; font-weight: 700; }
.jp-page-sub { margin: 4px 0 0; font-size: 14px; color: var(--jp-muted); }

/* Option cards */
.jp-options { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 16px; }
.jp-option { display: flex; align-items: center; gap: 16px; padding: 18px 20px; text-align: left; cursor: pointer;
  background: var(--jp-surface); border: 1px solid var(--jp-border); border-radius: 14px; color: var(--jp-text);
  transition: border-color .15s ease, background-color .15s ease; }
.jp-option:hover { border-color: var(--jp-teal-border); }
.jp-option--primary { background: rgba(15,179,173,0.1); border-color: var(--jp-teal-border); }
.jp-option--primary:hover { background: rgba(15,179,173,0.16); border-color: var(--jp-teal); }
.jp-option-tile { width: 48px; height: 48px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  border-radius: 12px; background: var(--jp-teal); color: #fff; }
.jp-option-tile--alt { background: rgba(14,165,233,0.16); color: #7dd3fc; }
.jp-option-text { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.jp-option-title { font-size: 16px; font-weight: 600; }
.jp-option-sub { margin-top: 3px; font-size: 13px; color: var(--jp-muted); }
.jp-option-chevron { color: var(--jp-muted); }

/* Toolbar */
.jp-toolbar { display: flex; align-items: center; gap: 12px; margin-top: 24px; }
.jp-search { position: relative; flex: 1; }
.jp-search--sm { flex: 0 1 300px; }
.jp-search-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: var(--jp-muted); pointer-events: none; }
.jp-input { width: 100%; padding: 10px 14px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.14);
  border-radius: 10px; color: var(--jp-text); font-size: 14px; outline: none; transition: border-color .15s ease, box-shadow .15s ease; }
.jp-input::placeholder { color: #7c8a9e; }
.jp-input:focus { border-color: var(--jp-teal); box-shadow: 0 0 0 3px rgba(15,179,173,0.2); }
.jp-search-input { height: 42px; padding-left: 38px; background: var(--jp-surface); }
.jp-filter-btn { height: 42px; }
.jp-filter-btn.is-open { border-color: var(--jp-teal); color: var(--jp-teal-light); }
.jp-filter-count { min-width: 18px; height: 18px; padding: 0 5px; border-radius: 999px; background: var(--jp-teal); color: #fff;
  font-size: 11px; display: inline-flex; align-items: center; justify-content: center; }

.jp-filters { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 20px; margin-top: 12px; padding: 16px 20px;
  background: var(--jp-surface); border: 1px solid var(--jp-border); border-radius: 12px; }
.jp-filters-title { margin: 0 0 10px; font-size: 13px; font-weight: 600; color: var(--jp-muted); }
.jp-checks { display: flex; flex-wrap: wrap; gap: 8px 18px; }
.jp-check { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; cursor: pointer; }
.jp-check input { width: 16px; height: 16px; accent-color: var(--jp-teal); cursor: pointer; }

/* Buttons */
.jp-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 10px 18px; border-radius: 10px;
  font-size: 14px; font-weight: 600; cursor: pointer; white-space: nowrap; border: 1px solid transparent;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease; }
.jp-btn:disabled { opacity: .6; cursor: not-allowed; }
.jp-btn-sm { padding: 7px 12px; font-size: 13px; }
.jp-btn-primary { background: var(--jp-teal); color: #fff; }
.jp-btn-primary:hover:not(:disabled) { background: var(--jp-teal-hover); }
.jp-btn-ghost { background: transparent; border-color: rgba(255,255,255,0.16); color: var(--jp-text); }
.jp-btn-ghost:hover:not(:disabled) { border-color: var(--jp-teal); color: var(--jp-teal-light); }
.jp-btn-danger { background: #dc2626; color: #fff; }
.jp-btn-danger:hover:not(:disabled) { background: #b91c1c; }
.jp-link { padding: 0; border: none; background: none; color: var(--jp-teal-light); font-size: 14px; font-weight: 500; cursor: pointer; }
.jp-link:hover { color: #fff; }
.jp-icon-btn { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 8px;
  border: none; background: transparent; color: var(--jp-muted); cursor: pointer; }
.jp-icon-btn:hover { background: rgba(255,255,255,0.06); color: #fff; }

/* Table */
.jp-table-card { margin-top: 16px; background: var(--jp-surface); border: 1px solid var(--jp-border); border-radius: 14px; overflow: hidden; }
.jp-table-scroll { overflow-x: auto; }
.jp-table { width: 100%; min-width: 900px; border-collapse: collapse; font-size: 14px; }
.jp-table th { padding: 14px 16px; text-align: left; font-size: 13px; font-weight: 600; color: var(--jp-muted);
  border-bottom: 1px solid var(--jp-border); white-space: nowrap; }
.jp-table td { padding: 14px 16px; border-bottom: 1px solid var(--jp-border); vertical-align: middle; }
.jp-table tbody tr:last-child td { border-bottom: none; }
.jp-table tbody tr:hover td { background: rgba(255,255,255,0.02); }
.jp-table .jp-center { text-align: center; }
.jp-table .jp-right { text-align: right; }
.jp-table td.jp-table-empty { text-align: center; padding: 36px 16px; color: var(--jp-muted); }
.jp-job-title { margin: 0; font-weight: 600; }
.jp-job-loc { margin: 4px 0 0; display: flex; align-items: center; gap: 4px; font-size: 12.5px; color: var(--jp-muted); }
.jp-status { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; }
.jp-status--open { background: rgba(34,197,94,0.15); color: #86efac; }
.jp-status--closed { background: rgba(248,113,113,0.14); color: #fca5a5; }
.jp-actions { display: inline-flex; align-items: center; gap: 8px; }
.jp-kebab { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.14); background: transparent; color: var(--jp-text); cursor: pointer; }
.jp-kebab:hover, .jp-kebab.is-open { border-color: var(--jp-teal); color: var(--jp-teal-light); }
.jp-table-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 16px;
  border-top: 1px solid var(--jp-border); font-size: 13px; }
.jp-pager { display: inline-flex; align-items: center; gap: 6px; }
.jp-pager button { min-width: 30px; height: 30px; padding: 0 6px; display: flex; align-items: center; justify-content: center;
  border-radius: 8px; border: 1px solid rgba(255,255,255,0.14); background: transparent; color: var(--jp-text); font-size: 13px; cursor: pointer; }
.jp-pager button:hover:not(:disabled) { border-color: var(--jp-teal); }
.jp-pager button.is-active { background: var(--jp-teal); border-color: var(--jp-teal); color: #fff; }
.jp-pager button:disabled { opacity: .4; cursor: not-allowed; }

/* ⋮ Menu (rendered inside .jp-root, position: fixed) */
.jp-menu { position: fixed; z-index: 1000; width: ${MENU_WIDTH}px; padding: 6px; background: #1b2433;
  border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; box-shadow: 0 16px 40px rgba(0,0,0,0.45); color: #f3f5f7; }
.jp-menu-item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 9px 10px; border: none; border-radius: 8px;
  background: transparent; color: #e2e8f0; font-size: 14px; text-align: left; cursor: pointer; }
.jp-menu-item:hover:not(:disabled) { background: rgba(255,255,255,0.06); color: #fff; }
.jp-menu-item:disabled { opacity: .45; cursor: not-allowed; }
.jp-menu-danger { color: #fca5a5; }
.jp-menu-danger:hover:not(:disabled) { background: rgba(248,113,113,0.12); color: #fecaca; }
.jp-danger-ico { color: #f87171; }
.jp-menu-caret { margin-left: auto; color: #94a3b8; transition: transform .15s ease; }
.jp-menu-caret.is-open { transform: rotate(90deg); }
.jp-submenu { margin: 2px 0 4px 26px; padding-left: 8px; border-left: 1px solid rgba(255,255,255,0.1); }
.jp-menu-sub { padding: 7px 10px; font-size: 13px; }
.jp-menu-check { margin-left: auto; color: #5eead4; }
.jp-dot { width: 10px; height: 10px; margin: 0 3px; border-radius: 50%; flex-shrink: 0; }
.jp-dot--open { background: #22c55e; }
.jp-dot--closed { background: #f87171; }

/* Dialog */
.jp-dialog-backdrop { position: fixed; inset: 0; z-index: 1100; display: flex; align-items: center; justify-content: center;
  padding: 16px; background: rgba(3,7,18,0.65); }
.jp-dialog { width: 100%; max-width: 400px; padding: 24px; text-align: center; background: #1b2433;
  border: 1px solid rgba(255,255,255,0.1); border-radius: 16px; box-shadow: 0 24px 60px rgba(0,0,0,0.5); color: #f3f5f7; }
.jp-dialog-icon { width: 48px; height: 48px; margin: 0 auto 12px; display: flex; align-items: center; justify-content: center;
  border-radius: 12px; background: rgba(248,113,113,0.14); color: #fca5a5; }
.jp-dialog-title { margin: 0; font-size: 18px; font-weight: 700; }
.jp-dialog-text { margin: 8px 0 20px; font-size: 14px; line-height: 1.5; color: #94a3b8; }
.jp-dialog-actions { display: flex; justify-content: center; gap: 10px; }

/* Project picker */
.jp-picker-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }
.jp-project-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 16px; }
.jp-project { display: flex; align-items: center; gap: 14px; padding: 16px; text-align: left; cursor: pointer;
  background: var(--jp-surface); border: 1px solid var(--jp-border); border-radius: 14px; color: var(--jp-text);
  transition: border-color .15s ease; }
.jp-project:hover { border-color: var(--jp-teal-border); }
.jp-project-text { flex: 1; min-width: 0; }
.jp-project-title { margin: 0; font-size: 15px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.jp-project-desc { margin: 3px 0 0; font-size: 13px; line-height: 1.45; color: var(--jp-muted);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.jp-project-chevron { color: var(--jp-muted); }
.jp-chip { display: inline-block; margin-top: 8px; padding: 2px 9px; border-radius: 999px; background: var(--jp-teal-soft);
  color: var(--jp-teal-light); font-size: 12px; }
.jp-avatar { width: 52px; height: 52px; flex-shrink: 0; overflow: hidden; border-radius: 12px; }
.jp-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.jp-avatar span { width: 100%; height: 100%; align-items: center; justify-content: center; font-weight: 700; font-size: 17px;
  background: var(--jp-teal-soft); color: var(--jp-teal-light); border: 1px solid var(--jp-teal-border); border-radius: 12px; box-sizing: border-box; }

.jp-skeleton { height: 88px; border-radius: 14px; background: linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.11) 50%, rgba(255,255,255,0.05) 75%);
  background-size: 200% 100%; animation: jp-shimmer 1.2s linear infinite; }
@keyframes jp-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

.jp-empty { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 40px 16px;
  background: var(--jp-surface); border: 1px solid var(--jp-border); border-radius: 16px; }
.jp-empty--inline { flex-direction: row; justify-content: center; gap: 12px; padding: 28px 16px; color: var(--jp-muted); font-size: 14px; }
.jp-empty--inline p { margin: 0; }
.jp-empty-icon { width: 60px; height: 60px; display: flex; align-items: center; justify-content: center; border-radius: 16px;
  background: var(--jp-teal-soft); color: var(--jp-teal); margin-bottom: 14px; }
.jp-empty-title { margin: 0; font-size: 16px; font-weight: 600; }
.jp-empty-sub { margin: 6px 0 18px; font-size: 14px; color: var(--jp-muted); }

/* Form screen */
.jp-form-page { position: relative; isolation: isolate; min-height: calc(100vh - 130px); overflow: hidden; }
.jp-form-bg { position: absolute; inset: 0; z-index: -2; background-size: cover; background-position: center; filter: brightness(1.05) saturate(1.1); }
.jp-form-overlay { position: absolute; inset: 0; z-index: -1; background:
  radial-gradient(ellipse at center, rgba(8,15,25,0.35) 0%, rgba(8,15,25,0.72) 100%),
  linear-gradient(180deg, rgba(8,15,25,0.25) 0%, rgba(8,15,25,0.1) 40%, rgba(8,15,25,0.45) 100%); }
.jp-form-wrap { max-width: 50rem; margin: 0 auto; padding: 1.5rem 1.5rem 2.5rem; }
.jp-form-card { padding: 28px 32px; background: rgba(13,20,32,0.8); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255,255,255,0.09); border-radius: 16px; box-shadow: 0 24px 60px rgba(0,0,0,0.5); }
.jp-form-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.jp-form-title { margin: 0; font-size: 22px; font-weight: 700; color: #fff; }
.jp-form-sub { margin: 4px 0 0; font-size: 14px; color: var(--jp-muted); }
.jp-selected-project { display: flex; align-items: center; gap: 14px; margin-top: 18px; padding: 14px;
  background: rgba(15,179,173,0.08); border: 1px solid var(--jp-teal-border); border-radius: 12px; }
.jp-selected-text { min-width: 0; }
.jp-selected-title { margin: 0; font-weight: 600; }
.jp-selected-desc { margin: 3px 0 0; font-size: 13px; color: var(--jp-muted); display: -webkit-box; -webkit-line-clamp: 2;
  -webkit-box-orient: vertical; overflow: hidden; }
.jp-form { margin-top: 8px; }
.jp-form-section { margin: 22px 0 12px; padding-bottom: 8px; border-bottom: 1px solid rgba(255,255,255,0.07);
  font-size: 13px; font-weight: 600; color: var(--jp-teal-light); }
.jp-grid-2 { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 16px; margin-bottom: 16px; }
.jp-field { margin-bottom: 16px; }
.jp-grid-2 .jp-field { margin-bottom: 0; }
.jp-field label { display: block; margin-bottom: 6px; font-size: 14px; font-weight: 600; color: var(--jp-text); }
.jp-req { margin-left: 3px; color: #f87171; }
.jp-select { appearance: none; -webkit-appearance: none; padding-right: 36px; cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 12px center; }
.jp-select option { background: #1b2433; color: #f3f5f7; }
.jp-root input[type="date"] { color-scheme: dark; }
.jp-icon-field { position: relative; }
.jp-field-icon { position: absolute; top: 12px; left: 13px; color: #7c8a9e; pointer-events: none; }
.jp-textarea { padding-left: 36px; resize: vertical; line-height: 1.5; }
.jp-form-actions { display: flex; justify-content: space-between; gap: 12px; margin-top: 8px; padding-top: 18px;
  border-top: 1px solid rgba(255,255,255,0.07); }

/* Responsive */
@media (max-width: 900px) {
  .jp-options, .jp-filters { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
  .jp-hero { height: 200px; }
  .jp-hero-content { padding: 0 16px; }
  .jp-hero-title { font-size: 23px; }
  .jp-body { padding: 16px 16px 32px; }
  .jp-toolbar { flex-wrap: wrap; }
  .jp-search--sm { flex: 1 1 100%; }
  .jp-grid-2 { grid-template-columns: 1fr; }
  .jp-form-wrap { padding: 16px 12px 32px; }
  .jp-form-card { padding: 20px 18px; }
  .jp-table-foot { flex-direction: column; align-items: flex-start; }
}
@media (prefers-reduced-motion: reduce) {
  .jp-skeleton { animation: none; }
  .jp-option, .jp-btn, .jp-project, .jp-menu-caret { transition: none; }
}
`;

export default JobPostings;